import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Package, Clock, DollarSign, TrendingUp, MapPin, Navigation, MessageCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { OrderChat } from '../../components/OrderChat';

export default function DriverDashboard() {
  const { api, user } = useAuth();
  const { formatPrice } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, deliveries: 0 });
  const [loading, setLoading] = useState(true);
  const [chatOrderId, setChatOrderId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, earningsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/drivers/earnings'),
      ]);
      setOrders(ordersRes.data);
      setEarnings({
        total: earningsRes.data.total_earnings,
        deliveries: earningsRes.data.total_deliveries,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeDeliveries = orders.filter(o => 
    ['assigned', 'picked_up', 'in_transit'].includes(o.status) && o.driver_id === user?.id
  );

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const statusColors = {
    assigned: 'bg-blue-100 text-blue-700',
    picked_up: 'bg-purple-100 text-purple-700',
    in_transit: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Deliveries</p>
                <p className="text-2xl font-bold text-slate-900">{activeDeliveries.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Package className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Deliveries</p>
                <p className="text-2xl font-bold text-slate-900">{earnings.deliveries}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Earnings</p>
                <p className="text-2xl font-bold text-slate-900">{formatPrice(earnings.total)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <p className="text-lg font-bold text-slate-900">
                  {user?.is_available ? 'Available' : 'Offline'}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl ${user?.is_available ? 'bg-green-50' : 'bg-slate-100'} flex items-center justify-center`}>
                <Navigation className={`w-6 h-6 ${user?.is_available ? 'text-green-500' : 'text-slate-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Deliveries */}
      <Card>
        <CardHeader>
          <CardTitle>Active Deliveries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-24 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : activeDeliveries.length > 0 ? (
            <div className="space-y-4">
              {activeDeliveries.map((order) => (
                <div 
                  key={order.id} 
                  className="bg-slate-50 rounded-xl p-4"
                  data-testid={`delivery-${order.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-slate-500">{order.restaurant_name}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                  </div>

                  <div className="flex items-start gap-2 text-sm text-slate-600 mb-4">
                    <MapPin className="w-4 h-4 text-orange-500 mt-0.5" />
                    <span>{order.delivery_address}, {order.delivery_city}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-bold text-orange-500">{formatPrice(order.delivery_fee)}</span>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-orange-500 border-orange-200 hover:bg-orange-50"
                        onClick={() => setChatOrderId(order.id)}
                        data-testid={`chat-${order.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      {order.status === 'assigned' && (
                        <Button 
                          size="sm" 
                          className="btn-primary"
                          onClick={() => updateOrderStatus(order.id, 'picked_up')}
                        >
                          Picked Up
                        </Button>
                      )}
                      {order.status === 'picked_up' && (
                        <Button 
                          size="sm" 
                          className="btn-primary"
                          onClick={() => updateOrderStatus(order.id, 'in_transit')}
                        >
                          Start Delivery
                        </Button>
                      )}
                      {order.status === 'in_transit' && (
                        <Button 
                          size="sm" 
                          className="btn-primary"
                          onClick={() => updateOrderStatus(order.id, 'delivered')}
                        >
                          Mark Delivered
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No active deliveries</p>
          )}
        </CardContent>
      </Card>

      {/* Order Chat Modal */}
      <OrderChat 
        orderId={chatOrderId}
        isOpen={!!chatOrderId}
        onClose={() => setChatOrderId(null)}
        participants={{ customer: true, restaurant: true, driver: true }}
      />
    </div>
  );
}
